
import React from 'react';
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TagInput } from "@/components/ui/tag-input";
import { useTagSuggestions } from "@/hooks/use-tag-suggestions";
import { Loader2, AlertCircle } from "lucide-react";

interface SaveDrawingDialogProps {
  title: string;
  onTitleChange: (title: string) => void;
  description: string;
  onDescriptionChange: (description: string) => void;
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  onSave: () => void;
  isSaving: boolean;
  hasTimedOut?: boolean;
  hasError?: boolean;
  dialogTitle?: string;
  isRetrying?: boolean;
  retryCount?: number;
  maxRetries?: number;
}

export const SaveDrawingDialog = ({
  title = "",
  onTitleChange,
  description = "",
  onDescriptionChange,
  selectedTags = [],
  onTagsChange,
  onSave,
  isSaving = false,
  hasTimedOut = false,
  hasError = false,
  dialogTitle = "Save Drawing",
  isRetrying = false,
  retryCount = 0,
  maxRetries = 3
}: SaveDrawingDialogProps) => {
  // Ensure selectedTags is always an array
  const safeTags = Array.isArray(selectedTags) ? selectedTags : [];
  const { popularTags, isLoading } = useTagSuggestions();

  const handleSave = () => {
    if (!title?.trim() || isSaving) {
      return;
    }
    onSave();
  };

  return (
    <DialogContent className="sm:max-w-[450px] p-6 rounded-xl border-0 shadow-xl bg-white/95 backdrop-blur-sm">
      <DialogHeader className="mb-2">
        <DialogTitle className="text-xl font-semibold text-gray-800 tracking-tight">{dialogTitle}</DialogTitle>
      </DialogHeader>
      
      <div className="space-y-5 py-2">
        <div className="space-y-2.5">
          <label htmlFor="title" className="text-sm font-medium text-gray-600">
            Title
          </label>
          <Input
            id="title"
            placeholder="Untitled Project"
            value={title || ""}
            onChange={(e) => onTitleChange(e.target.value)}
            className="w-full border-gray-200 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 transition-all duration-200"
            disabled={isSaving}
          />
        </div>

        <div className="space-y-2.5">
          <label htmlFor="description" className="text-sm font-medium text-gray-600">
            Description
          </label>
          <Textarea
            id="description"
            placeholder="Add a description (optional)"
            value={description || ""}
            onChange={(e) => onDescriptionChange(e.target.value)}
            className="min-h-[100px] resize-none border-gray-200 focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 transition-all duration-200"
            disabled={isSaving}
          />
        </div>

        <div className="space-y-2.5">
          <label className="text-sm font-medium text-gray-600">
            Tags
          </label>
          <TagInput
            selectedTags={safeTags}
            onTagsChange={onTagsChange}
            suggestions={popularTags}
            placeholder="Add tags (press Enter)"
            disabled={isSaving}
            maxTags={10}
          />
        </div>
      </div>

      {(isRetrying || hasTimedOut) && (
        <div className="mt-2 text-amber-600 text-sm flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          <span>{`Retry attempt ${retryCount}/${maxRetries}...`}</span>
        </div>
      )}

      <DialogFooter className="pt-4 sm:pt-5">
        <Button
          onClick={handleSave}
          disabled={!title?.trim() || isSaving}
          className={`w-full sm:w-auto gap-2 py-2.5 px-5 rounded-lg ${
            hasTimedOut 
              ? 'bg-amber-500 hover:bg-amber-600 text-white' 
              : hasError
              ? 'bg-red-500 hover:bg-red-600 text-white'
              : 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-md hover:shadow-lg transition-all duration-300'
          }`}
        >
          {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
          {hasTimedOut && <AlertCircle className="h-4 w-4" />}
          {hasError && <AlertCircle className="h-4 w-4" />}
          {isSaving ? "Saving..." : 
          hasTimedOut ? "Retrying..." : 
          hasError ? "Retry Save" : 
          "Save Drawing"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};
